import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Announcement, LibraryAnnouncement} from '../app/types'
import {api} from '../common/api'
import {useSelector} from 'react-redux'
import {selectedSiteSelector} from '../authentication/redux'
import SpinnableSubmitButton from "../common/form/SpinnableSubmitButton";
import CancelButton from "../common/form/CancelButton";
import {Dialog, DialogActions, DialogContent, DialogTitle, Paper} from "@mui/material";
import ErrorAlert from "../common/components/ErrorAlert";
import {useTableWithAdjustableOrder} from "../common/components/table/useTableWithAdjustableOrder";
import {getAutocompleteLibraryAnnouncements} from "../common/util/helpers";
import {useFormSubmission} from "../common/servercall/useFormSubmission";
import {useForm} from "react-hook-form";
import FormRow from "../common/form/FormRow";
import FormFieldBox from "../common/form/FormFieldBox";
import FormGroup from "../common/form/FormGroup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import * as yup from "yup";

type ImportFromLibraryForm = {
    announcementsToAdd: LibraryAnnouncement[]
}

const formatAnnouncement = (announcement: LibraryAnnouncement) => {
    return announcement.name
}

export const ImportFromLibrary = ({onClose, existingAnnouncements}: {onClose: () => void, existingAnnouncements: Announcement[]}) => {
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)

    const { control, handleSubmit, setError } = useForm<ImportFromLibraryForm>({
        resolver: yupResolver(yup.object()),
        defaultValues: { announcementsToAdd: [] }
    })

    const onSubmit = useCallback(
        async (formData: ImportFromLibraryForm) => {
            if (!site ) return
            if (formData.announcementsToAdd.length !== 0) {
                await api.post(`/api/site/${site.productId}/announcement/copy`, {
                    uuids: formData.announcementsToAdd.map(announcement => announcement.uuid)
                })
            }
            onClose()
        },
        [site, onClose]
    )

    const libraryOptionParams = useMemo(() =>
            getAutocompleteLibraryAnnouncements(t, existingAnnouncements.map(a => a.name), site)
        , [site, t, existingAnnouncements])

    const { submit, isSubmitting, serverError } = useFormSubmission<ImportFromLibraryForm>(onSubmit, setError)

    const announcementUuid = (announcement: LibraryAnnouncement) => {
        return announcement.uuid
    }

    const { selector: AnnouncementSelector, table: AnnouncementSelection } = useTableWithAdjustableOrder<LibraryAnnouncement>('announcementsToAdd', libraryOptionParams, control, formatAnnouncement, announcementUuid, t('announcements.select-library-files'), true)

    return (
        <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth={'sm'}>
            <form onSubmit={handleSubmit(submit)}>
                <DialogTitle>{t('announcements.import-from-library')}</DialogTitle>
                <DialogContent>
                    <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError}/>
                    <FormGroup label={''} fullWidth={true}>
                        <FormRow>
                            <FormFieldBox mdSize={8} lgSize={6}>
                                {AnnouncementSelector}
                            </FormFieldBox>
                        </FormRow>
                        <FormRow>
                            <FormFieldBox mdSize={12}>
                                <Paper elevation={2}>
                                    <AnnouncementSelection />
                                </Paper>
                            </FormFieldBox>
                        </FormRow>
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <SpinnableSubmitButton label={t('general.import')} showSpinner={isSubmitting} />
                    <CancelButton onClick={onClose} />
                </DialogActions>
            </form>
        </Dialog>
    )
}
